/**
 * Non-money display helpers: timestamps, shortened platform ids, dispute-deadline urgency.
 *
 * WHY a module of its own: these are pure functions the tables share, and keeping them out of
 * components means they can be unit-tested without a DOM. The deadline thresholds are the same
 * ones the engine uses for R10 (docs/reference/CONSTANTS.md "Engine thresholds") so the UI does
 * not disagree with the discrepancy the backend raises.
 */
import { NO_VALUE } from "./money";

/** R10 DISPUTE_DEADLINE — evidence due in under this many days is an alert in the engine too. */
export const DISPUTE_WARNING_DAYS = 3;
const MS_PER_DAY = 86_400_000;

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC",
});
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "2-digit", timeZone: "UTC" });

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** UTC on purpose: settlement timestamps are platform-side facts, not the viewer's wall clock. */
export function formatDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${DATE_TIME_FORMAT.format(d)} UTC` : NO_VALUE;
}

export function formatDate(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? DATE_FORMAT.format(d) : NO_VALUE;
}

/**
 * Platform ids are long and mostly boilerplate ("gid://shopify/ShopifyPaymentsPayout/1234").
 * Keep the part that identifies the object; the full value stays in a title attribute.
 */
export function shortExternalId(externalId: string | null | undefined, maxLength = 18): string {
  if (!externalId) return NO_VALUE;
  const tail = externalId.includes("/") ? externalId.slice(externalId.lastIndexOf("/") + 1) : externalId;
  return tail.length > maxLength ? `…${tail.slice(tail.length - maxLength)}` : tail;
}

/** Whole days from `now` to `iso`; negative when the date has passed. Null when there is no date. */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  const d = parse(iso);
  return d === null ? null : Math.floor((d.getTime() - now.getTime()) / MS_PER_DAY);
}

export type DeadlineTone = "NONE" | "PAST" | "SOON" | "OK";

export function deadlineTone(iso: string | null | undefined, now: Date = new Date()): DeadlineTone {
  const days = daysUntil(iso, now);
  if (days === null) return "NONE";
  if (days < 0) return "PAST";
  return days < DISPUTE_WARNING_DAYS ? "SOON" : "OK";
}

export function deadlineLabel(iso: string | null | undefined, now: Date = new Date()): string {
  const days = daysUntil(iso, now);
  if (days === null) return NO_VALUE;
  if (days < 0) return `${-days}d overdue`;
  if (days === 0) return "due today";
  return `in ${days}d`;
}

/** Enum-ish API values are shown verbatim (they are the domain vocabulary); this only softens
 *  them for prose, e.g. a page heading. */
export function humanizeEnum(value: string): string {
  const lower = value.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
